import { ArrowDown, Loader2, Pause, Play } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { APP_NAME } from '~/constants/config/env';
import { useCountUp } from '~/features/content/hooks/use-count-up';
import { SPECIMEN_PRODUCTS } from '~/features/public-home/constants/specimen-products';
import type { SpecimenProduct } from '~/features/public-home/constants/specimen-products';
import { cn } from '~/utils/cn';

/** How long the raw record is "read" (scan sweep + skeleton) before the reveal. */
const READ_MS = 1400;
/** Reveal choreography: chips stagger in and the attribute count ticks up over this window. */
const CHIP_START_MS = 250;
const CHIP_STAGGER_MS = 140;
const REVEAL_MS = CHIP_START_MS + CHIP_STAGGER_MS * 7;
/** How long the enriched result stays on screen before the next product starts. */
const HOLD_MS = 4200;

type Phase = 'reading' | 'enriched';

interface CycleState {
  index: number;
  phase: Phase;
  /** Bumped on every (re)start so keyed children replay their entrance animations. */
  run: number;
}

/** Whether the viewer asked for less motion — the specimen then stays still. */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Drives the specimen loop: read (READ_MS) → enriched (REVEAL_MS + HOLD_MS) → next
 * product. Stops while `paused`; never runs under reduced motion, where every product
 * shows straight in its enriched state. `select` jumps to a product and replays it.
 */
function useSpecimenCycle(count: number, paused: boolean) {
  const [reducedMotion] = useState(prefersReducedMotion);
  const initial: CycleState = { index: 0, phase: reducedMotion ? 'enriched' : 'reading', run: 0 };
  const [state, setState] = useState(initial);

  useEffect(() => {
    if (paused || reducedMotion) return undefined;
    const delay = state.phase === 'reading' ? READ_MS : REVEAL_MS + HOLD_MS;
    const id = window.setTimeout(() => {
      setState((s) =>
        s.phase === 'reading'
          ? { ...s, phase: 'enriched' }
          : { index: (s.index + 1) % count, phase: 'reading', run: s.run + 1 },
      );
    }, delay);
    return () => {
      window.clearTimeout(id);
    };
  }, [state, paused, reducedMotion, count]);

  const select = (index: number) => {
    setState((s) => ({ index, phase: reducedMotion ? 'enriched' : 'reading', run: s.run + 1 }));
  };

  return { ...state, select, reducedMotion };
}

/**
 * Hero illustration built from real UI rather than a screenshot: a product as a thin
 * merchant-feed record, then "live" enrichment — the record is scanned, the result
 * streams in (copy, chips, an attribute count ticking up) and the loop moves on to the
 * next sample. Pauses on hover/focus and via its pause button (WCAG 2.2.2).
 */
export function ProductSpecimen() {
  const { t } = useTranslation('auth');
  const [userPaused, setUserPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const { index, phase, run, select, reducedMotion } = useSpecimenCycle(
    SPECIMEN_PRODUCTS.length,
    userPaused || hovered,
  );
  const reading = phase === 'reading';

  return (
    <div
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
    >
      <figure className="bg-card relative w-full rounded-3xl border p-2 shadow-[0_24px_60px_-28px_oklch(0.2429_0.0304_283.91/0.35)]">
        <figcaption className="sr-only">
          {t('home.specimen.caption', { appName: APP_NAME })}
        </figcaption>

        {/* Raw record. All products share one grid cell so the card never changes height. */}
        <div
          className={cn(
            'bg-neutral-20 relative grid overflow-hidden rounded-2xl px-5 pt-4 pb-6 transition-opacity duration-500',
            !reading && 'opacity-80',
          )}
        >
          {reading && (
            <div
              aria-hidden
              className="animate-specimen-scan pointer-events-none absolute inset-0 bg-[linear-gradient(100deg,transparent_20%,var(--gradient-promo-lavender)_50%,transparent_80%)] opacity-60"
            />
          )}
          {SPECIMEN_PRODUCTS.map((product, i) => (
            <BeforeRecord key={product.id} product={product} active={i === index} />
          ))}
        </div>

        {/* Connector badge straddles the seam; spins while the record is being read. */}
        <div className="relative z-10 -my-3 flex justify-center" aria-hidden>
          <span
            className={cn(
              'bg-card text-brand flex h-8 w-8 items-center justify-center rounded-full border shadow-sm transition-transform duration-300',
              !reading && 'scale-110',
            )}
          >
            {reading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowDown className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 h-4 w-4" />
            )}
          </span>
        </div>

        <div className="relative grid rounded-2xl border px-5 pt-6 pb-5">
          {SPECIMEN_PRODUCTS.map((product, i) => (
            <EnrichedRecord
              key={i === index ? `${product.id}-${String(run)}` : product.id}
              product={product}
              active={i === index}
              reading={reading}
              animate={!reducedMotion}
            />
          ))}
        </div>
      </figure>

      {/* Manual controls: pick a sample, or stop the loop entirely. */}
      <div className="mt-4 flex items-center justify-center gap-1">
        {SPECIMEN_PRODUCTS.map((product, i) => (
          <Button
            key={product.id}
            variant="ghost"
            size="sm"
            className="h-7 w-7 cursor-pointer p-0"
            aria-label={t('home.specimen.showExample', {
              name: t(`home.specimen.products.${product.id}.name`),
            })}
            aria-pressed={i === index}
            onClick={() => {
              select(i);
            }}
          >
            <span
              className={cn(
                'block h-2 rounded-full transition-all duration-300',
                i === index ? 'bg-brand w-5' : 'bg-neutral-60 w-2',
              )}
            />
          </Button>
        ))}
        {!reducedMotion && (
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground ml-1 h-7 w-7 cursor-pointer p-0"
            aria-label={userPaused ? t('home.specimen.play') : t('home.specimen.pause')}
            onClick={() => {
              setUserPaused((p) => !p);
            }}
          >
            {userPaused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
          </Button>
        )}
      </div>
    </div>
  );
}

interface BeforeRecordProps {
  product: SpecimenProduct;
  /** Inactive records stay in the grid cell (invisible) to reserve the tallest height. */
  active: boolean;
}

/** The thin merchant-feed version of a sample product. */
function BeforeRecord({ product, active }: BeforeRecordProps) {
  const { t } = useTranslation('auth');
  const base = `home.specimen.products.${product.id}`;

  return (
    <div
      aria-hidden={!active}
      className={cn(
        'transition-opacity duration-500 [grid-area:1/1]',
        active ? 'opacity-100' : 'invisible opacity-0',
      )}
    >
      <div className="text-muted-foreground flex items-baseline justify-between gap-4 text-sm">
        <span>{t('home.specimen.beforeLabel')}</span>
        <span>{t('home.specimen.attributeCount', { count: product.beforeCount })}</span>
      </div>
      <p className="text-muted-foreground mt-3 font-medium tracking-wide">
        {t(`${base}.beforeTitle`)}
      </p>
      <p className="text-muted-foreground mt-1 text-sm">{t(`${base}.beforeCategory`)}</p>
      <ul
        className="mt-3 flex flex-wrap gap-2"
        aria-label={t('home.specimen.beforeAttributesLabel')}
      >
        {product.beforeAttributes.map((key) => (
          <li
            key={key}
            className="bg-neutral-40 text-muted-foreground rounded-full px-3 py-1 text-sm"
          >
            {t(`${base}.beforeAttributes.${key}`)}
          </li>
        ))}
      </ul>
    </div>
  );
}

interface EnrichedRecordProps {
  product: SpecimenProduct;
  active: boolean;
  /** While true the active record shows skeleton lines in place of the result. */
  reading: boolean;
  animate: boolean;
}

/**
 * The enriched version of a sample product. The active one is remounted per run (keyed
 * by the parent), so its staggered entrance replays every time it comes round.
 */
function EnrichedRecord({ product, active, reading, animate }: EnrichedRecordProps) {
  const { t } = useTranslation('auth');
  const base = `home.specimen.products.${product.id}`;
  const showResult = active && !reading;
  const enter = animate ? 'animate-in fade-in slide-in-from-bottom-2 fill-mode-both' : '';

  return (
    <div aria-hidden={!active} className={cn('[grid-area:1/1]', !active && 'invisible')}>
      <div className="flex items-baseline justify-between gap-4 text-sm">
        <span className="text-muted-foreground">
          {active && reading
            ? t('home.specimen.enrichingLabel', { appName: APP_NAME })
            : t('home.specimen.afterLabel', { appName: APP_NAME })}
        </span>
        <span className="text-brand font-semibold tabular-nums">
          {showResult ? (
            <AttributeCount from={product.beforeCount} to={product.afterCount} />
          ) : (
            t('home.specimen.attributeCount', { count: product.beforeCount })
          )}
        </span>
      </div>

      {/* Result and skeleton share a cell so swapping them never shifts the layout. */}
      <div className="grid">
        <div className={cn('[grid-area:1/1]', !showResult && 'invisible')}>
          <p
            className={cn(
              'mt-3 text-xl leading-snug font-semibold tracking-tight',
              showResult && enter,
            )}
          >
            {t(`${base}.afterTitle`)}
          </p>
          <p
            className={cn('text-muted-foreground mt-1 text-sm', showResult && enter)}
            style={{ animationDelay: '80ms' }}
          >
            {t(`${base}.afterCategory`)}
          </p>
          <p
            className={cn(
              'text-muted-foreground mt-3 text-sm leading-relaxed',
              showResult && enter,
            )}
            style={{ animationDelay: '160ms' }}
          >
            {t(`${base}.afterDescription`)}
          </p>
          <ul className="mt-4 flex flex-wrap gap-2" aria-label={t('home.specimen.attributesLabel')}>
            {product.attributes.map((key, i) => (
              <li
                key={key}
                style={{ animationDelay: `${String(CHIP_START_MS + i * CHIP_STAGGER_MS)}ms` }}
                className={cn(
                  'bg-brand-100 text-brand-accent rounded-full px-3 py-1 text-sm font-medium',
                  showResult &&
                    animate &&
                    'animate-in fade-in zoom-in-75 fill-mode-both duration-300',
                )}
              >
                {t(`${base}.attributes.${key}`)}
              </li>
            ))}
          </ul>
        </div>

        {active && reading && (
          <div className="flex flex-col pt-3 [grid-area:1/1]" aria-hidden>
            <Skeleton className="bg-neutral-40 h-6 w-11/12" />
            <Skeleton className="bg-neutral-40 mt-2 h-6 w-2/3" />
            <Skeleton className="bg-neutral-40 mt-3 h-4 w-1/3" />
            <Skeleton className="bg-neutral-40 mt-4 h-4 w-full" />
            <Skeleton className="bg-neutral-40 mt-2 h-4 w-4/5" />
            <div className="mt-5 flex flex-wrap gap-2">
              {[20, 24, 18, 28].map((w) => (
                <Skeleton
                  key={w}
                  className="bg-neutral-40 h-7 rounded-full"
                  style={{ width: `${String(w * 4)}px` }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Ticks the attribute count from the feed's number up to the enriched one during the
 * reveal. `useCountUp` eases out (front-loaded), so it runs longer than the chip stagger
 * to land on the final number alongside the last chip rather than well ahead of it.
 */
function AttributeCount({ from, to }: { from: number; to: number }) {
  const { t } = useTranslation('auth');
  const delta = useCountUp(to - from, REVEAL_MS * 2) ?? 0;
  return <>{t('home.specimen.attributeCount', { count: Math.round(from + delta) })}</>;
}
